import { AnalyticsConfig } from '@/types/overview';
import { usePersistedJsonState } from '@superfunnel/react-hooks';
import {
  AnalyticsFilters,
  Domain,
  DynamicPage,
  DynamicPageVariant,
  EmptyAnalyticsFilters,
  Website,
} from '@superfunnel/types';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  where,
} from 'firebase/firestore';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { db } from '../Firebase';
import { getAnalyticsByDates } from '../services/AnalyticsService';
import { dynamicPageVariantsColRef } from '../services/DynamicPageVariantsService';
import {
  analyticsRangeToDates,
  emptyConverter,
  getDynamicPagePrimaryDomain,
  getDynamicPageRelevantDomains,
} from '../utils';
import { useDashboardContext } from './DashboardContext';
import { UserContext } from './UserContext';

interface DynamicPageContextProps {
  selectedDynamicPage: QueryDocumentSnapshot<DynamicPage> | undefined;
  websites: QueryDocumentSnapshot<Website>[] | undefined;
  variants: QueryDocumentSnapshot<DynamicPageVariant>[] | undefined;
  customRangeAnalytics: any | undefined;
  hasChangesToDeploy: boolean | undefined;
  primaryDomain: QueryDocumentSnapshot<Domain> | undefined;
  relevantDomains: QueryDocumentSnapshot<Domain>[] | undefined;
  analyticsConfig: AnalyticsConfig;
  /** Dynamic page-specific analytics filters (separate from account-level filters) */
  analyticsFilters: AnalyticsFilters;
  setAnalyticsFilters: (filters: AnalyticsFilters) => void;
  resetAnalyticsFilters: () => void;
}

export const DynamicPageContext = createContext<DynamicPageContextProps | null>(
  null,
);

export function DynamicPageContextProvider(props: any) {
  const location = useLocation();

  const { selectedAccount } = useContext(UserContext);
  const { analyticsRange, domains, dynamicPages } = useDashboardContext();

  const [websites, setWebsites] = useState<QueryDocumentSnapshot<Website>[]>();
  const [customRangeAnalytics, setCustomRangeAnalytics] = useState<any>();
  const [variants, setVariants] =
    useState<QueryDocumentSnapshot<DynamicPageVariant>[]>();

  const [config, _] = useState<AnalyticsConfig>(defaultDPAnalyticsConfig);

  // Dynamic page-specific analytics filters (persisted separately from account-level)
  const [analyticsFilters, setAnalyticsFilters] =
    usePersistedJsonState<AnalyticsFilters>(
      'dynamicPageAnalyticsFilters',
      EmptyAnalyticsFilters,
    );

  const resetAnalyticsFilters = useCallback(() => {
    setAnalyticsFilters(EmptyAnalyticsFilters);
  }, [setAnalyticsFilters]);

  const selectedDynamicPage = useMemo(() => {
    const dynamicPageId = location.pathname.split('/')[4];
    if (dynamicPageId === 'pages') {
      const dynamicPageId = location.pathname.split('/')[5];
      return dynamicPages?.find((a) => a.id === dynamicPageId);
    }
    return dynamicPages?.find((a) => a.id === dynamicPageId);
  }, [dynamicPages, location]);

  const hasChangesToDeploy = useMemo(() => {
    return selectedDynamicPage?.data().hasChangesToPublish;
  }, [selectedDynamicPage]);

  const relevantDomains = useMemo(() => {
    if (!domains || !selectedDynamicPage) return domains;

    return getDynamicPageRelevantDomains(domains, selectedDynamicPage?.data());
  }, [domains, selectedDynamicPage]);

  const primaryDomain = useMemo(() => {
    if (!relevantDomains) return;

    return getDynamicPagePrimaryDomain(relevantDomains);
  }, [relevantDomains]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage) {
      setWebsites(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = selectedDynamicPage.id;

    const websitesColRef = collection(
      db,
      'accounts',
      accountId,
      'campaigns',
      dynamicPageId,
      'websites',
    ).withConverter(emptyConverter<Website>());
    const unsubscribe = onSnapshot(
      query(
        websitesColRef,
        where('status', 'in', ['draft', 'active', 'disabled', 'creating']),
        orderBy('createdAt', 'asc'),
      ),
      (websites) => {
        setWebsites(websites.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    if (!selectedAccount || !selectedDynamicPage) {
      setVariants(undefined);
      return;
    }

    const accountId = selectedAccount.id;
    const dynamicPageId = selectedDynamicPage.id;

    const variantsColRef = dynamicPageVariantsColRef(accountId, dynamicPageId);
    const unsubscribe = onSnapshot(
      query(
        variantsColRef,
        where('status', 'in', ['draft', 'active', 'disabled']),
        orderBy('createdAt', 'asc'),
      ),
      (variants) => {
        setVariants(variants.docs);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount, selectedDynamicPage]);

  useEffect(() => {
    setCustomRangeAnalytics(undefined);

    if (!selectedAccount || !selectedDynamicPage) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);

    let relevant = true;
    getAnalyticsByDates(
      selectedAccount.id,
      'campaign',
      selectedDynamicPage.id,
      startDate,
      endDate,
    ).then((res) => {
      if (!relevant) return;

      setCustomRangeAnalytics(res);
    });

    return () => {
      relevant = false;
    };
  }, [selectedAccount, selectedDynamicPage, analyticsRange]);

  return (
    <DynamicPageContext.Provider
      value={{
        selectedDynamicPage,
        websites,
        variants,
        customRangeAnalytics,
        hasChangesToDeploy,
        relevantDomains,
        primaryDomain,
        analyticsConfig: config,
        analyticsFilters,
        setAnalyticsFilters,
        resetAnalyticsFilters,
      }}
    >
      {props.children}
    </DynamicPageContext.Provider>
  );
}

export function useDynamicPageContext() {
  const context = useContext(DynamicPageContext);
  if (!context) {
    throw new Error(
      'useDynamicPageContext must be used within a DynamicPageContextProvider',
    );
  }
  return context;
}

export const defaultDPAnalyticsConfig: AnalyticsConfig = [
  {
    id: 'kpi_visitors',
    title: 'Total Visitors',
    kind: 'metric',
    metrics: ['visitors'],
    aggregate: 'sum',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'kpi_conversions',
    title: 'Total Conversions',
    kind: 'metric',
    metrics: ['conversions'],
    aggregate: 'sum',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'kpi_cvr',
    title: 'Avg Conversion Rate',
    kind: 'metric',
    metrics: ['cvr'],
    aggregate: 'avg',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_variants',
    title: 'Top Variants',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'variantId',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_referrers',
    title: 'Top Referrers',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'referrerDomain',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_ad_networks',
    title: 'Top Ad Networks',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'adNetworkProvider',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'trend_main',
    title: 'Trends Over Time',
    kind: 'area',
    metrics: ['visitors', 'conversions', 'cvr', 'leads'],
    segment: 'day',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 12 },
    },
  },
  {
    id: 'breakdown_device',
    title: 'Device Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'device',
    minLegendItems: ['mobile', 'desktop', 'tablet'],
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_browser',
    title: 'Browser Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'browser',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_os',
    title: 'OS Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'os',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_country',
    title: 'Country Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'country',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_city',
    title: 'City Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'city',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_bots',
    title: 'Bot Traffic Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'botName',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
];
